import { useEffect, useState } from 'react';
import { Link } from 'react-scroll';
import { Scale, Phone, Instagram, MessageCircle, Mail, MapPin, Clock, Shield, Users, Award, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    {
      title: 'Aposentadoria por Idade',
      description: 'Ocorre quando o segurado do INSS atinge a idade mínima exigida pela legislação previdenciária, aos segurados urbanos que completarem 65 anos de idade, se homem e 61 anos e 6 meses, se mulher.'
    },
    {
      title: 'Aposentadoria por Tempo de Contribuição',
      description: 'Ocorre quando o segurado do INSS atinge o tempo mínimo de contribuição exigido pela legislação previdenciária.'
    },
    {
      title: 'Aposentadoria Especial',
      description: 'É concedido ao trabalhador que exerce atividade exposta a agentes nocivos, que podem causar algum prejuízo à sua saúde e integridade física.'
    },
    {
      title: 'Aposentadoria Rural',
      description: 'É devido ao trabalhador rural, agricultor familiar ou pescador artesanal quando atinge a idade mínima exigida e comprova o tempo de atividade rural, sem a necessidade de comprovação de contribuições ao INSS.'
    },
    {
      title: 'Salário Maternidade',
      description: 'Benefício devido para a pessoa que se afastar da sua atividade por motivo de nascimento de filho(a), aborto não criminoso, adoção ou guarda judicial para fins de adoção.'
    },
    {
      title: 'Benefício de Prestação Continuada (BPC)',
      description: 'O BPC é a garantia de um salário mínimo mensal ao idoso acima de 65 anos ou à pessoa com deficiência que esteja impossibilitada de participar de forma plena e efetiva em sociedade.'
    },
    {
      title: 'Auxílio Doença',
      description: 'É um benefício concedido pelo INSS ao segurado que ficar incapacitado para o seu trabalho ou atividade habitual por mais de 15 dias consecutivos.'
    },
    {
      title: 'Auxílio Acidente',
      description: 'É um benefício previdenciário indenizatório devido aos segurados que sofrem qualquer categoria de acidente que resultam em sequelas que diminuam a sua capacidade para o trabalho.'
    },
    {
      title: 'Planejamento Previdenciário',
      description: 'O planejamento previdenciário é a análise antecipada da vida profissional e contributiva para definir a melhor forma de se aposentar, no momento certo e com o maior benefício possível. Avalia contribuições passadas, presentes e futuras, garantindo segurança e previsibilidade para o futuro.'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // EmailJS configuration
      const serviceId = 'service_j4dmyri';
      const templateId = 'template_r1vwo0b';
      const publicKey = 'K3RtA_JOutVtlywwJ';

      // Prepare template params
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
        to_email: 'contato@amaraladvocacia.com.br'
      };

      // Send email via EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente ou entre em contato via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img src="https://customer-assets.emergentagent.com/job_juridicoamaral/artifacts/nqhmmg06_LOGOo.png" alt="Amaral Advocacia" className="h-12" />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="inicio" smooth={true} duration={500} className="text-gray-700 hover:text-[#1e3a8a] cursor-pointer transition-colors font-medium">
                Início
              </Link>
              <Link to="quem-somos" smooth={true} duration={500} className="text-gray-700 hover:text-[#1e3a8a] cursor-pointer transition-colors font-medium">
                Quem Somos
              </Link>
              <Link to="servicos" smooth={true} duration={500} className="text-gray-700 hover:text-[#1e3a8a] cursor-pointer transition-colors font-medium">
                Serviços
              </Link>
              <Link to="contato" smooth={true} duration={500} className="text-gray-700 hover:text-[#1e3a8a] cursor-pointer transition-colors font-medium">
                Contato
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <a href="https://wa.me/5518996588771" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="pt-32 pb-20 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-8">
              <Scale className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Amaral Advocacia e Consultoria Jurídica
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Especialistas em Direito Previdenciário, dedicados a proteger seus direitos e garantir seu futuro
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="contato" smooth={true} duration={500}>
                <Button size="lg" className="bg-white text-[#1e3a8a] hover:bg-gray-100 rounded-full px-8 text-lg">
                  Agende uma Consulta
                </Button>
              </Link>
              <Link to="servicos" smooth={true} duration={500}>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 text-lg">
                  Conheça Nossos Serviços
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white" />
        </div>
      </section>

      {/* Quem Somos */}
      <section id="quem-somos" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">Quem Somos</h2>
              <div className="w-24 h-1 bg-[#1e3a8a] mx-auto rounded-full"></div>
            </div>
            
            <div className="space-y-8">
              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#1e3a8a] rounded-full flex items-center justify-center">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Nossa Missão</h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        Amaral Advocacia e Consultoria Jurídica é especializado em Direito Previdenciário, dedicado a orientar e proteger os direitos de quem busca segurança e tranquilidade no presente e no futuro. Atuamos de forma estratégica e personalizada, oferecendo soluções claras, responsáveis e atualizadas para aposentadorias, benefícios previdenciários e planejamento previdenciário.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#1e3a8a] rounded-full flex items-center justify-center">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Nossa Especialista</h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        A Dra. Rafaela Amaral, advogada formada pela Faculdade de Filosofia, Ciências e Letras – FAFIPE e inscrita na Ordem dos Advogados do Brasil (OAB/SP 517.078), especialista em Direito Previdenciário e Planejamento Previdenciário.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#1e3a8a] rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Nosso Compromisso</h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        Nosso compromisso é prestar um atendimento humano, ético e transparente, sempre focado na melhor solução para cada cliente.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section id="servicos" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">Nossos Serviços</h2>
            <div className="w-24 h-1 bg-[#1e3a8a] mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Oferecemos soluções completas em Direito Previdenciário para garantir seus direitos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-[#1e3a8a] transition-all duration-300 hover:shadow-xl group">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#1e3a8a] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Scale className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-[#1e3a8a] transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-base leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contato */}
      <section id="contato" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-4">Entre em Contato</h2>
              <div className="w-24 h-1 bg-[#1e3a8a] mx-auto rounded-full mb-6"></div>
              <p className="text-xl text-gray-600">
                Estamos prontos para atendê-lo e garantir seus direitos previdenciários
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Formulário */}
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900">Envie sua Mensagem</CardTitle>
                  <CardDescription className="text-base">Preencha o formulário e retornaremos em breve</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium">Nome Completo</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-2 border-gray-300 focus:border-[#1e3a8a] focus:ring-[#1e3a8a]"
                        placeholder="Seu nome"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-700 font-medium">E-mail</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2 border-gray-300 focus:border-[#1e3a8a] focus:ring-[#1e3a8a]"
                        placeholder="seu@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 font-medium">Telefone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 border-gray-300 focus:border-[#1e3a8a] focus:ring-[#1e3a8a]"
                        placeholder="(18) 99999-9999"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-gray-700 font-medium">Mensagem</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="mt-2 border-gray-300 focus:border-[#1e3a8a] focus:ring-[#1e3a8a]"
                        placeholder="Como podemos ajudá-lo?"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-full py-6 text-lg font-semibold"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Informações de Contato */}
              <div className="space-y-6">
                <Card className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-[#1e3a8a] rounded-full flex items-center justify-center">
                          <Mail className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">E-mail</h3>
                        <a href="mailto:contato@amaraladvocacia.com.br" className="text-[#1e3a8a] hover:underline text-lg">
                          contato@amaraladvocacia.com.br
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
                          <MessageCircle className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">WhatsApp</h3>
                        <a href="https://wa.me/5518996588771" target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:underline text-lg">
                          (18) 99658-8771
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                          <Instagram className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Instagram</h3>
                        <a href="https://www.instagram.com/_amaraladvocacia" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline text-lg">
                          @_amaraladvocacia
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg bg-[#1e3a8a] text-white">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                          <Clock className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold mb-2">Horário de Atendimento</h3>
                        <p className="text-blue-100">Segunda a Sexta</p>
                        <p className="text-blue-100">08:00 às 18:00</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e3a8a] text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="https://customer-assets.emergentagent.com/job_juridicoamaral/artifacts/nqhmmg06_LOGOo.png" alt="Amaral Advocacia" className="h-16 mb-4 brightness-0 invert" />
              <p className="text-blue-100">
                Especialistas em Direito Previdenciário
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Links Rápidos</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="inicio" smooth={true} duration={500} className="text-blue-100 hover:text-white cursor-pointer transition-colors">
                    Início
                  </Link>
                </li>
                <li>
                  <Link to="quem-somos" smooth={true} duration={500} className="text-blue-100 hover:text-white cursor-pointer transition-colors">
                    Quem Somos
                  </Link>
                </li>
                <li>
                  <Link to="servicos" smooth={true} duration={500} className="text-blue-100 hover:text-white cursor-pointer transition-colors">
                    Serviços
                  </Link>
                </li>
                <li>
                  <Link to="contato" smooth={true} duration={500} className="text-blue-100 hover:text-white cursor-pointer transition-colors">
                    Contato
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Contato</h3>
              <div className="space-y-3">
                <p className="text-blue-100">
                  <Mail className="w-4 h-4 inline mr-2" />
                  contato@amaraladvocacia.com.br
                </p>
                <p className="text-blue-100">
                  <Phone className="w-4 h-4 inline mr-2" />
                  (18) 99658-8771
                </p>
                <div className="flex space-x-4 mt-4">
                  <a href="https://www.instagram.com/_amaraladvocacia" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a href="https://wa.me/5518996588771" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                    <MessageCircle className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-blue-700 pt-8 text-center">
            <p className="text-blue-100">
              © 2025 Amaral Advocacia e Consultoria Jurídica. Todos os direitos reservados.
            </p>
            <p className="text-blue-200 text-sm mt-2">
              OAB/SP 517.078 - Dra. Rafaela Amaral
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;